import {
  useArchiveCannedWorkTasksMutation,
  useRestoreCannedWorkTasksMutation,
  useReviewCannedWorkTaskVersionsMutation,
  useSetCannedWorkTasksTaskGroupMutation,
} from "@common/api/quoting/canned"
import type {
  CannedWorkTaskDetailPublic,
  CannedWorkTaskReviewFailure,
  CannedWorkTaskReviewsPublic,
  QuoteTaskGroupPublic,
} from "@common/client"
import { useHasPermission } from "@common/hooks/use-permissions"
import { handleError } from "@common/utils"
import { useNavigate } from "@tanstack/react-router"
import {
  FloatingAction,
  FloatingActionBar,
} from "@/components/layout/floating-action-bar"
import useCustomToast from "@/hooks/use-custom-toast"
import { ChoiceMenu } from "@/routes/_layout/quotes_/$quoteId/-components/items-table"
import {
  useTaskGroups,
  useTaskGroupsReadable,
} from "@/routes/_layout/quotes_/$quoteId/-lib/task-groups"
import Check from "~icons/material-symbols/check"
import Close from "~icons/material-symbols/close"
import DeleteOutline from "~icons/material-symbols/delete-outline"

type Decision = "approved" | "rejected"

const DONE: Record<Decision, string> = {
  approved: "Approved",
  rejected: "Rejected",
}

const STALE = "changed or were reviewed by someone else"

const WHY: Record<CannedWorkTaskReviewFailure, string> = {
  not_found: STALE,
  already_reviewed: STALE,
  outdated: STALE,
  not_allowed: "aren't yours to review",
  needs_more_approvals: "need another approval",
}

function why(result: CannedWorkTaskReviewsPublic): string {
  return [...new Set(result.failed.map((failure) => WHY[failure.reason]))].join(
    " or ",
  )
}

function counted(count: number): string {
  return count === 1 ? "1 template task" : `${count} template tasks`
}

function groupedMessage(
  count: number,
  taskGroup: QuoteTaskGroupPublic | undefined,
): [string, string] {
  return taskGroup
    ? ["Task group set", `Moved ${counted(count)} to ${taskGroup.name}.`]
    : ["Task group cleared", `Removed ${counted(count)} from their task group.`]
}

export function SelectionBar({
  tasks,
  deletable,
  onSelect,
}: {
  tasks: CannedWorkTaskDetailPublic[]
  deletable: boolean
  onSelect: (taskIds: string[]) => void
}): React.ReactElement {
  const navigate = useNavigate()
  const review = useReviewCannedWorkTaskVersionsMutation()
  const archiveMany = useArchiveCannedWorkTasksMutation()
  const restoreMany = useRestoreCannedWorkTasksMutation()
  const assign = useSetCannedWorkTasksTaskGroupMutation()
  const taskGroups = useTaskGroups()
  const showToast = useCustomToast()
  const canReview = useHasPermission("canned_work_package:review")
  const canArchive = useHasPermission("canned_work_package:delete")
  const canCreate = useHasPermission("canned_work_package:create")
  const taskGroupsReadable = useTaskGroupsReadable()
  const live = tasks.filter((task) => !task.deleted_at)
  const groupable =
    live.length > 0 && live.every((task) => task.canned_work_package_id)
  const archived = tasks.filter((task) => task.deleted_at)
  const pending = live.filter(
    (task) => task.latest_version.review_status === "pending",
  )

  const change = (
    mutation: typeof archiveMany | typeof restoreMany,
    targets: CannedWorkTaskDetailPublic[],
    done: string,
  ) =>
    mutation.mutate(
      {
        cannedWorkTaskBulkIds: {
          canned_work_task_ids: targets.map((task) => task.id),
        },
      },
      {
        onSuccess: ({ skipped }) => {
          showToast(
            done,
            `${done} ${counted(targets.length - skipped.length)}.`,
            skipped.length > 0 ? "error" : "success",
          )
          onSelect(skipped)
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  const decide = (status: Decision) =>
    review.mutate(
      {
        cannedWorkTaskReviews: {
          status,
          versions: pending.map((task) => ({
            canned_work_task_id: task.id,
            canned_work_task_version_id: task.latest_version.id,
          })),
        },
      },
      {
        onSuccess: (result) => {
          report(result, status)
          onSelect(result.failed.map((failure) => failure.canned_work_task_id))
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  const group = (taskGroupId: string | null) =>
    assign.mutate(
      {
        cannedWorkTaskBulkTaskGroup: {
          canned_work_task_ids: live.map((task) => task.id),
          task_group_id: taskGroupId,
        },
      },
      {
        onSuccess: () => {
          showToast(
            ...groupedMessage(
              live.length,
              taskGroups.find((taskGroup) => taskGroup.id === taskGroupId),
            ),
            "success",
          )
          onSelect([])
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  const report = (result: CannedWorkTaskReviewsPublic, status: Decision) =>
    result.failed.length > 0
      ? showToast(
          `${counted(result.failed.length)} not reviewed`,
          `${DONE[status]} ${counted(result.reviewed.length)}. The rest ${why(result)}, and are still selected.`,
          "error",
        )
      : showToast(
          DONE[status],
          `${DONE[status]} ${counted(result.reviewed.length)}.`,
          "success",
        )

  return (
    <FloatingActionBar
      count={tasks.length}
      archive={
        canArchive
          ? {
              count: live.length,
              pending: archiveMany.isPending,
              onClick: () => change(archiveMany, live, "Archived"),
            }
          : undefined
      }
      restore={
        canArchive
          ? {
              count: archived.length,
              pending: restoreMany.isPending,
              onClick: () => change(restoreMany, archived, "Restored"),
            }
          : undefined
      }
      onClear={() => onSelect([])}
    >
      {canArchive && deletable && live.length > 0 && (
        <FloatingAction
          hint={`Delete ${live.length}`}
          onClick={() =>
            navigate({
              from: "/templates/tasks",
              to: "/templates/tasks/delete",
              search: (previous) => ({
                ...previous,
                ids: live.map((task) => task.id),
              }),
            })
          }
        >
          <DeleteOutline />
          Delete
        </FloatingAction>
      )}
      {canCreate && taskGroupsReadable && groupable && (
        <ChoiceMenu
          label="Task group"
          noun="task groups"
          emptyLabel="No task group"
          choices={taskGroups}
          disabled={assign.isPending}
          onPick={group}
        />
      )}
      {canReview && live.length > 0 && pending.length === 0 && (
        <span className="text-muted-foreground">No drafts to review</span>
      )}
      {canReview && pending.length > 0 && (
        <>
          <FloatingAction
            hint={`Approve ${pending.length}`}
            disabled={review.isPending}
            onClick={() => decide("approved")}
          >
            <Check />
            Approve
          </FloatingAction>
          <FloatingAction
            hint={`Reject ${pending.length}`}
            disabled={review.isPending}
            onClick={() => decide("rejected")}
          >
            <Close />
            Reject
          </FloatingAction>
        </>
      )}
    </FloatingActionBar>
  )
}
